import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useUserPreferences } from '@/hooks/useUserPreferences';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getTheme, setThemePreference, isLoading, preferences } = useUserPreferences();
  
  // Estado local do tema - inicia com localStorage para evitar flash
  // Usa a mesma chave do useUserPreferences para consistência
  const [theme, setThemeState] = useState<Theme>(() => {
    const cached = localStorage.getItem('user_preferences_cache');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.theme === 'light' || parsed?.theme === 'dark') {
          return parsed.theme;
        }
      } catch (e) {
        // Ignora erro de parse
      }
    }
    return 'light';
  });

  // Sincroniza com preferências do banco quando carregadas
  useEffect(() => {
    if (!isLoading && preferences.theme) {
      setThemeState(preferences.theme);
    }
  }, [isLoading, preferences.theme]);

  // Aplica tema no DOM (localStorage é gerenciado pelo useUserPreferences)
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    // Persiste no banco de dados
    setThemePreference(newTheme);
  }, [setThemePreference]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
